// src/app/forgot-password/forgot-password.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, from } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import { ToastService } from '../toast/toast.service';
import { encryptClientPayload } from '../../utils/client-payload-crypto.util';
import { getAuthPayloadEncryptionSecret } from '../../config/auth-payload-encryption';

export type ForgotStage = 1 | 2 | 3 | 4;

export interface ForgotState {
  stage: ForgotStage;
  email?: string;
  code?: string;
}

export interface ForgotPasswordResponse {
  success?: boolean;
  message?: string;
  ok?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ForgotPasswordService {
  private state$ = new BehaviorSubject<ForgotState>({ stage: 1 });

  stage$: Observable<ForgotState> = this.state$.asObservable();

  constructor(
    private apiService: ApiService,
    private toastService: ToastService,
  ) {}

  get current() {
    return this.state$.getValue();
  }

  setStage(stage: ForgotStage) {
    this.state$.next({ ...this.current, stage });
  }

  setEmail(email: string) {
    this.state$.next({ ...this.current, email });
  }

  setCode(code: string) {
    this.state$.next({ ...this.current, code });
  }

  /**
   * Request password reset code
   * POST /auth/forgot-password
   */
  requestOtp(email: string): Observable<{ ok: boolean }> {
    // Enable error toast for API errors
    return this.apiService
      .post<ForgotPasswordResponse>('/auth/forgot-password', { email }, true)
      .pipe(
        map(() => ({ ok: true })),
        tap(() => {
          this.setEmail(email);
          this.setStage(2);
          this.toastService.success('Reset code sent to your email', 'Success');
        }),
      );
  }

  /**
   * Verify reset code
   * POST /auth/verify-reset-code
   */
  verifyOtp(code: string): Observable<{ ok: boolean }> {
    const email = this.current.email;
    if (!email) {
      throw new Error('Email is required');
    }

    return from(encryptClientPayload(code, getAuthPayloadEncryptionSecret())).pipe(
      switchMap((encryptedCode) =>
        this.apiService.post<ForgotPasswordResponse>(
          '/auth/verify-reset-code',
          { email, code: encryptedCode },
          true,
        ),
      ),
      map(() => ({ ok: true })),
      tap(() => {
        this.setCode(code);
        this.setStage(3);
        this.toastService.success('Code verified successfully', 'Success');
      }),
    );
  }

  /**
   * Reset password with code
   * POST /auth/reset-password
   */
  resetPassword(newPassword: string): Observable<{ ok: boolean }> {
    const email = this.current.email;
    const code = this.current.code;

    if (!email || !code) {
      throw new Error('Email and code are required');
    }

    const secret = getAuthPayloadEncryptionSecret();
    return forkJoin({
      encryptedCode: from(encryptClientPayload(code, secret)),
      encryptedNewPassword: from(encryptClientPayload(newPassword, secret)),
    }).pipe(
      switchMap(({ encryptedCode, encryptedNewPassword }) =>
        this.apiService.post<ForgotPasswordResponse>(
          '/auth/reset-password',
          { email, code: encryptedCode, new_password: encryptedNewPassword },
          true,
        ),
      ),
      map(() => ({ ok: true })),
      tap(() => {
        this.setStage(4);
        this.toastService.success('Password reset successfully', 'Success');
      }),
    );
  }

  restart() {
    this.state$.next({ stage: 1 });
  }
}
